import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { themeColors } from '../theme/colors';

export type AppButtonVariant = 'primary' | 'secondary' | 'cancel' | 'danger' | 'accent';

export interface AppButtonProps extends Omit<ButtonProps, 'color' | 'variant'> {
    variant?: AppButtonVariant;
    /** Use for loading state - shows spinner if provided */
    loading?: boolean;
}

const variantStyles: Record<AppButtonVariant, ButtonProps['sx']> = {
    primary: {
        backgroundColor: themeColors.primary,
        color: 'white',
        boxShadow: `0 4px 12px ${themeColors.primaryShadow}`,
        '&:hover': {
            backgroundColor: themeColors.primaryHover,
            boxShadow: `0 6px 16px ${themeColors.primaryShadow}`,
        },
        '&.Mui-disabled': {
            backgroundColor: '#94a3b8',
            color: '#fff',
        },
    },
    secondary: {
        color: themeColors.primary,
        borderColor: themeColors.primary,
        '&:hover': {
            backgroundColor: themeColors.primaryLight,
            borderColor: themeColors.primaryHover,
            color: themeColors.primaryHover,
        },
    },
    cancel: {
        borderColor: '#9ca3af',
        color: '#4b5563',
        textTransform: 'none',
        '&:hover': {
            borderColor: '#4b5563',
            color: '#1f2937',
            backgroundColor: 'rgba(0,0,0,0.02)',
        },
    },
    danger: {
        backgroundColor: '#dc2626',
        color: 'white',
        '&:hover': {
            backgroundColor: '#b91c1c',
        },
    },
    accent: {
        backgroundColor: '#09c2c1',
        color: 'white',
        '&:hover': {
            backgroundColor: '#08847e',
        },
    },
};

export const AppButton: React.FC<AppButtonProps> = ({
    variant = 'primary',
    loading = false,
    children,
    disabled,
    startIcon,
    sx,
    ...rest
}) => {
    const isOutlined = variant === 'secondary' || variant === 'cancel';
    const muiVariant = isOutlined ? 'outlined' : 'contained';

    const baseSx = variantStyles[variant];

    return (
        <Button
            variant={muiVariant}
            disabled={disabled || loading}
            startIcon={loading ? undefined : startIcon}
            sx={[
                {
                    textTransform: 'none',
                    fontWeight: 600,
                },
                ...(Array.isArray(baseSx) ? baseSx : [baseSx]),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...rest}
        >
            {loading ? (
                <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    {children}
                </>
            ) : (
                children
            )}
        </Button>
    );
};
